// frontend/src/hooks/useServiceTypes.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { serviceTypeService, ServiceTypeFilters, ServiceTypeFormData } from '@/lib/api/serviceTypeService';

export const useServiceTypes = (filters: ServiceTypeFilters = {}) => {
  return useQuery({
    queryKey: ['service-types', filters],
    queryFn: async () => {
      const response = await serviceTypeService.getServiceTypes(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    staleTime: 300000,
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const useServiceType = (id: number) => {
  return useQuery({
    queryKey: ['service-type', id],
    queryFn: async () => {
      const response = await serviceTypeService.getServiceTypeById(id);
      if (response.success && response.data) {
        return response.data.service_type;
      }
      throw new Error(response.message);
    },
    enabled: !!id,
    staleTime: 30000,
  });
};

export const useCreateServiceType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceTypeFormData) => {
      const response = await serviceTypeService.createServiceType(data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-types'] });
    },
  });
};

export const useUpdateServiceType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ServiceTypeFormData }) => {
      const response = await serviceTypeService.updateServiceType(id, data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-types'] });
      queryClient.invalidateQueries({ queryKey: ['service-type', variables.id] });
    },
  });
};

export const useDeleteServiceType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await serviceTypeService.deleteServiceType(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-types'] });
    },
  });
};

export const useToggleServiceTypeStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await serviceTypeService.toggleServiceTypeStatus(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-types'] });
      queryClient.invalidateQueries({ queryKey: ['service-type', variables] });
    },
  });
};

export const useApproveServiceType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await serviceTypeService.approveServiceType(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-types'] });
      queryClient.invalidateQueries({ queryKey: ['service-type', variables] });
    },
  });
};

export const useRejectServiceType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await serviceTypeService.rejectServiceType(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-types'] });
      queryClient.invalidateQueries({ queryKey: ['service-type', variables] });
    },
  });
};

export const usePendingServiceTypes = (filters: ServiceTypeFilters = {}) => {
  return useQuery({
    queryKey: ['pending-service-types', filters],
    queryFn: async () => {
      const response = await serviceTypeService.getPendingServiceTypes(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    staleTime: 30000,
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

